<template>
  <div class="my-4">
    <input
      v-if="localInstallationPoint.isAdvancedRegistrationMeter"
      :id="'deviceGroup_' + deviceGroup.id"
      :value="deviceGroup.id"
      type="checkbox"
      :checked="allDevicesSelected"
      @change="toggleArmsAssignmentByDeviceGroup($event)"
    />
    <input
      v-else
      :id="'deviceGroup_' + deviceGroup.id"
      v-model="localInstallationPoint.billedInRoomGroupId"
      :value="deviceGroup.id"
      type="radio"
    />

    <label :for="'deviceGroup_' + deviceGroup.id" class="font-normal">
      <span class="mb-2 mr-2"><em class="mdi mdi-view-grid"></em></span
      >{{ deviceGroup.label }}
    </label>
  </div>

  <div v-if="localInstallationPoint.isAdvancedRegistrationMeter">
    <div
      v-for="installationPointByDeviceGroup in getInstallationPointsByDeviceGroup"
      :key="installationPointByDeviceGroup.id"
      class="device-list-element"
    >
      <input
        :id="'device_' + installationPoint.id"
        :checked="
          installationPointByDeviceGroup.advancedRegistrationById ===
          localInstallationPoint.id
        "
        type="checkbox"
        @change="toggleArmAssignment($event, installationPointByDeviceGroup)"
      />
      <label :for="'device_' + installationPoint.id">
        <span class="has-text-weight-bold">
          <span
            v-if="installationPointByDeviceGroup.isAdvancedRegistrationMeter"
            class="mdi mdi-cellphone"
          ></span>
          <span v-else class="mdi mdi-cellphone"></span>
        </span>
        {{
          getActiveDevice(installationPointByDeviceGroup.activeDeviceId)
            .serialNumber
        }}
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { DeviceGroup } from '@/components/Forms/DeviceFormComponents/AdvancedRegistraionMeter/DeviceGroup.vue';
import { computed, WritableComputedRef } from 'vue';
import { deviceTypes } from '@/enums/device';
import { entityTypes } from '@/enums/generic';
import { DeviceClasses } from '@/models/devices/Device';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { instanceToInstance } from 'class-transformer';

const props = defineProps<{
  device: DeviceClasses;
  installationPoint: InstallationPoint;
  deviceGroup: DeviceGroup;
  armInstallationPoints?: InstallationPoint[];
  updateArmInstallationPoints?: (
    newArmInstallationPoints: InstallationPoint[]
  ) => void;
}>();

const emits = defineEmits(['update:installationPoint']);

const localInstallationPoint: WritableComputedRef<InstallationPoint> = computed(
  {
    get() {
      return props.installationPoint;
    },
    set(value) {
      emits('update:installationPoint', value);
    },
  }
);

const getActiveDevice = (deviceId: string) => {
  return useDeviceStore().devices.get(deviceId);
};

const getInstallationPointsByDeviceGroup: WritableComputedRef<
  InstallationPoint[]
> = computed(() => {
  const deviceTypeFilter = (node: InstallationPoint) => {
    if (props.device.deviceType === deviceTypes.HeatMeter) {
      return (
        node.supportedDeviceType === props.device.deviceType ||
        node.supportedDeviceType === deviceTypes.HeatCostAllocator
      );
    } else {
      return node.supportedDeviceType === props.device.deviceType;
    }
  };

  const installationPointsByDeviceGroup =
    useEntitiesStore().getAllEntitiesByCriteria(
      (node: InstallationPoint) =>
        node.type === entityTypes.installationPoint &&
        node.activeDeviceId !== undefined &&
        node.activeDeviceId !== props.device.id &&
        node.billedInRoomGroupId === props.deviceGroup.id &&
        deviceTypeFilter(node) &&
        node.id !== getCentralWarmWaterHeatMeterId()
    );

  return installationPointsByDeviceGroup.map((i) => instanceToInstance(i));
});

const getCentralWarmWaterHeatMeterId = () => {
  const centralArm = useInstallationPointStore().getCentralWarmWaterHeatMeter();
  return centralArm.length ? centralArm[0].id : undefined;
};

const toggleArmsAssignmentByDeviceGroup = (event: any) => {
  const installationPoints = getInstallationPointsByDeviceGroup;
  const newArmInstallationPoints = [...props.armInstallationPoints];

  installationPoints.value.forEach((installationPoint) =>
    setAdvancedRegistration(event, installationPoint, newArmInstallationPoints)
  );
  props.updateArmInstallationPoints(newArmInstallationPoints);
};

const toggleArmAssignment = (
  event: any,
  installationPoint: InstallationPoint
) => {
  const newArmInstallationPoints = [...props.armInstallationPoints];
  setAdvancedRegistration(event, installationPoint, newArmInstallationPoints);

  props.updateArmInstallationPoints(newArmInstallationPoints);
};

const setAdvancedRegistration = (
  event: any,
  installationPoint: InstallationPoint,
  newArmInstallationPoints: InstallationPoint[]
) => {
  if (event.target.checked) {
    installationPoint.advancedRegistrationById = props.installationPoint.id;
  } else {
    installationPoint.advancedRegistrationById = undefined;
  }

  const index = props.armInstallationPoints.findIndex(
    (arm) => arm.id === installationPoint.id
  );

  if (index > -1) {
    newArmInstallationPoints[index] = installationPoint;
  } else {
    newArmInstallationPoints.push(installationPoint);
  }
};

const allDevicesSelected = computed(() => {
  const installationPoints = getInstallationPointsByDeviceGroup;
  if (installationPoints.value.length === 0) {
    return false;
  }
  return installationPoints.value.every((d) =>
    props.armInstallationPoints.some((arm) => arm.id === d.id)
  );
});
</script>

<style>
.device-list-element {
  margin-left: 31px;
  margin-bottom: 15px;
}
</style>
